import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useScheduleStore } from '../store';
import { useIpState } from './useIpState';
import { setAnalyticsContext, resetAnalyticsContext, toCustodyRatio } from '../services/analytics';

/**
 * Keeps the analytics common-dimensions in sync with app state.
 *
 * Mount this once near the top of the component tree (inside all providers).
 * It does not render anything - it only calls `setAnalyticsContext()` when
 * relevant values change so every subsequent `trackEvent()` picks them up.
 */
export const useAnalyticsContext = (): void => {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const schedule = useScheduleStore((s) => s.schedule);
  const calculateParentTime = useScheduleStore((s) => s.calculateParentTime);
  const detectedState = useIpState();

  // User identity & plan
  useEffect(() => {
    if (user) {
      setAnalyticsContext({ user_id: user.id, user_plan: subscription?.plan ?? 'basic' });
    } else {
      resetAnalyticsContext();
    }
  }, [user, subscription?.plan]);

  // Detected US state
  useEffect(() => {
    if (detectedState) {
      setAnalyticsContext({ user_state: detectedState });
    }
  }, [detectedState]);

  // Template + custody ratio (derived from active schedule)
  useEffect(() => {
    if (!schedule || !schedule.parents.length) return;
    setAnalyticsContext({ template_id: schedule.templateId ?? null });

    if (schedule.timeBlocks.length > 0) {
      const stats = calculateParentTime(schedule.parents[0].id);
      setAnalyticsContext({ custody_ratio: toCustodyRatio(stats.percentage) });
    }
  }, [schedule, schedule?.templateId, schedule?.timeBlocks.length, schedule?.parents, calculateParentTime]);
};
